/**
 * Track last user action for error debugging context
 */

interface UserAction {
  type: string;
  target: string;
  timestamp: number;
  route: string;
}

interface UserActionTrackingHandlers {
  click?: EventListener;
  submit?: EventListener;
  focus?: EventListener;
  popstate?: EventListener;
}

declare global {
  interface Window {
    __userActionTrackingHandlers?: UserActionTrackingHandlers;
    __userActionTrackingInterval?: number;
  }
}

/**
 * Sanitize text to remove PII
 */
const sanitizeText = (text: string): string => {
  if (!text) return "";

  // Truncate first
  let sanitized = text.substring(0, 30);

  // Remove emails
  sanitized = sanitized.replace(
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
    "[email]"
  );

  // Remove phone-like patterns
  sanitized = sanitized.replace(
    /\b\d{3}[-.\s]?\d{3}[-.\s]?\d{4}\b/g,
    "[phone]"
  );

  // Remove credit card patterns
  sanitized = sanitized.replace(
    /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g,
    "[card]"
  );

  // Remove long number sequences
  sanitized = sanitized.replace(/\b\d{6,}\b/g, "[number]");

  return sanitized.trim();
};

/**
 * Check if input is sensitive (password, credit card, etc.)
 */
const isSensitiveInput = (element: HTMLElement): boolean => {
  if (element.tagName === "INPUT" || element.tagName === "TEXTAREA") {
    const input = element as HTMLInputElement;
    const type = input.type?.toLowerCase();
    const name = input.name?.toLowerCase();

    // Skip password, credit card, SSN fields
    if (type === "password" || type === "tel") return true;
    if (
      name?.includes("password") ||
      name?.includes("card") ||
      name?.includes("cvv") ||
      name?.includes("ssn")
    )
      return true;
  }

  return false;
};

let lastAction: UserAction = {
  type: "page_load",
  target: "",
  timestamp: Date.now(),
  route: getNormalizedRoute(),
};

/**
 * Normalize ID-heavy routes before persisting them to Datadog/PostHog.
 * Prevents high-cardinality `last_user_action.route/target` values.
 *
 * Examples:
 * - /appointment/details/123 -> /appointment/details/:id
 * - /async-funnel/<uuid>/... -> /async-funnel/:id/...
 */
function normalizeRoute(pathname: string): string {
  return pathname
    .replace(
      /\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi,
      "/:id"
    )
    .replace(/\/\d+/g, "/:id");
}

function getNormalizedRoute(): string {
  return normalizeRoute(window.location.pathname);
}

/**
 * Get the last user action
 */
export const getLastUserAction = (): UserAction => {
  return { ...lastAction };
};

/**
 * Get formatted last user action for logging
 */
export const getFormattedLastUserAction = (): string => {
  const timeSince = Math.round((Date.now() - lastAction.timestamp) / 1000);
  return `${lastAction.type} ${lastAction.target} (${timeSince}s ago)`;
};

/**
 * Initialize user action tracking
 * Call this once on app initialization
 */
export const initUserActionTracking = (): void => {
  // If init is called multiple times (e.g. tests/HMR), avoid duplicate handlers.
  cleanupUserActionTracking();

  // Track clicks
  const clickHandler: EventListener = (e) => {
    const target = e.target as HTMLElement;

    // Skip if sensitive input
    if (isSensitiveInput(target)) {
      lastAction = {
        type: "click",
        target: `${target.tagName.toLowerCase()}[sensitive]`,
        timestamp: Date.now(),
        route: getNormalizedRoute(),
      };
      return;
    }

    const tagName = target.tagName?.toLowerCase() || "unknown";
    const id = target.id ? `#${target.id}` : "";
    // Some non-HTMLElement targets (e.g. SVG) expose `className` as a different type.
    // Guard before splitting to avoid runtime crashes.
    const rawClassName = (target as unknown as { className?: unknown })
      .className;
    const classAttr =
      typeof rawClassName === "string"
        ? rawClassName
        : (target as unknown as Element).getAttribute?.("class") ?? "";
    const className = classAttr ? `.${classAttr.split(" ")[0]}` : "";

    const textContent = (target as unknown as { textContent?: string | null })
      .textContent;
    const text = sanitizeText(textContent || "");

    lastAction = {
      type: "click",
      target: `${tagName}${id}${className} "${text}"`.trim(),
      timestamp: Date.now(),
      route: getNormalizedRoute(),
    };
  };

  // Track form submissions
  const submitHandler: EventListener = (e) => {
    const target = e.target as HTMLFormElement;
    const id = target.id ? `#${target.id}` : "";
    const className = target.className
      ? `.${target.className.split(" ")[0]}`
      : "";

    lastAction = {
      type: "form_submit",
      target: `form${id}${className}`,
      timestamp: Date.now(),
      route: getNormalizedRoute(),
    };
  };

  // Track input focus (useful for debugging form errors)
  const focusHandler: EventListener = (e) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
      const input = target as HTMLInputElement;
      const id = input.id ? `#${input.id}` : "";
      const name = input.name ? `[name=${input.name}]` : "";
      const type = input.type ? `[type=${input.type}]` : "";

      lastAction = {
        type: "focus_input",
        target: `${input.tagName.toLowerCase()}${id}${name}${type}`,
        timestamp: Date.now(),
        route: getNormalizedRoute(),
      };
    }
  };

  // Register document-level listeners (capture enabled so we catch events early)
  document.addEventListener("click", clickHandler, true);
  document.addEventListener("submit", submitHandler, true);
  document.addEventListener("focus", focusHandler, true);

  // Track route changes (for SPA navigation)
  let lastRoute = window.location.pathname;
  const checkRouteChange = () => {
    const currentRoute = window.location.pathname;
    if (currentRoute !== lastRoute) {
      lastAction = {
        type: "navigation",
        target: normalizeRoute(currentRoute),
        timestamp: Date.now(),
        route: getNormalizedRoute(),
      };
      lastRoute = currentRoute;
    }
  };

  // Check for route changes periodically (for hash-based routing or manual pushState)
  const routeCheckInterval = window.setInterval(checkRouteChange, 500);

  // Also listen to popstate for back/forward navigation
  const popstateHandler: EventListener = () => {
    lastAction = {
      type: "navigation_back",
      target: getNormalizedRoute(),
      timestamp: Date.now(),
      route: getNormalizedRoute(),
    };
  };

  window.addEventListener("popstate", popstateHandler);

  // Store handlers/interval for cleanup so we can undo everything reliably.
  window.__userActionTrackingHandlers = {
    click: clickHandler,
    submit: submitHandler,
    focus: focusHandler,
    popstate: popstateHandler,
  };
  window.__userActionTrackingInterval = routeCheckInterval;
};

/**
 * Cleanup user action tracking (for tests or app teardown)
 */
export const cleanupUserActionTracking = (): void => {
  const handlers = window.__userActionTrackingHandlers;
  const intervalId = window.__userActionTrackingInterval;

  if (handlers?.click) {
    document.removeEventListener("click", handlers.click, true);
  }
  if (handlers?.submit) {
    document.removeEventListener("submit", handlers.submit, true);
  }
  if (handlers?.focus) {
    document.removeEventListener("focus", handlers.focus, true);
  }
  if (handlers?.popstate) {
    window.removeEventListener("popstate", handlers.popstate);
  }

  if (typeof intervalId === "number") {
    window.clearInterval(intervalId);
  }

  window.__userActionTrackingHandlers = undefined;
  window.__userActionTrackingInterval = undefined;
};
